import { forwardRef, useCallback, useId } from 'react'

import { TextInput } from '../TextInput'

import classNames from 'classnames'
import styles from './FileUploadInput.module.css'
import { FileUploadInputProps } from './types'
import { Icon } from '../Icon'
import { IconButton } from '../IconButton'
import { LayoutStack } from '../layouts'
import { useForwardedRef } from '../../hooks/'

// Maybe get rid of the internal files...

// Helper functions to handle both File and FileList
const isFile = (value: unknown): value is File => {
  return value instanceof File
}

type Files = FileList | File | null | undefined

const getFileCount = (files: Files): number => {
  if (!files) return 0
  if (isFile(files)) return 1
  return files.length
}

const getFileName = (files: Files, index: number = 0): string => {
  if (!files) return ''
  if (isFile(files)) return index === 0 ? files.name : ''
  return files[index]?.name || ''
}

const FileUploadInput = forwardRef<HTMLInputElement, FileUploadInputProps>(
  (
    {
      label,
      name,
      description,
      error,
      warning,
      id,
      required,
      className,
      invisibleLabel,
      accept,
      multiple = false,
      maxSize,
      fullWidth,
      placeholder = '',
      buttonText = '',
      onFileChange,
      disabled,
      readOnly,
      multipleFilesSelectedLabel,
      onChange,
      files,
      tabIndex,
      displayText: displayText_,
      ...props
    }: FileUploadInputProps,
    ref
  ) => {
    const generatedId = useId()
    const inputId = id || name || generatedId
    const [fileInputRef, wrappedRef] = useForwardedRef(ref)

    const getDisplayText = (): string => {
      const fileCount = getFileCount(files)

      if (fileCount === 0) {
        return displayText_ ?? placeholder
      }

      if (fileCount === 1) {
        return getFileName(files, 0)
      }

      return `${fileCount} ${multipleFilesSelectedLabel}`
    }

    const handleFileInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files

        // Validate file size if maxSize is provided
        if (maxSize && selectedFiles) {
          for (let i = 0; i < selectedFiles.length; i++) {
            const file = selectedFiles[i]
            if (file && file.size > maxSize) {
              console.warn(`File ${file.name} exceeds maximum size of ${maxSize} bytes`)
              return
            }
          }
        }

        onFileChange?.(selectedFiles)

        onChange?.(event)
      },
      [maxSize, onFileChange, onChange]
    )

    const handleClearFiles = useCallback(() => {
      onFileChange?.(null)
    }, [onFileChange])

    const handleBrowseClick = useCallback(() => {
      if (!disabled && !readOnly) {
        fileInputRef.current?.click()
      }
    }, [disabled, fileInputRef, readOnly])

    const displayText = getDisplayText()
    const hasFiles = getFileCount(files) > 0 || displayText !== placeholder

    return (
      <LayoutStack space="50" className={className}>
        <TextInput
          label={label}
          name={`${name}-display`}
          description={description}
          error={error}
          warning={warning}
          id={`${inputId}-display`}
          required={required}
          invisibleLabel={invisibleLabel}
          disabled_={true}
          value={displayText}
          readOnly={true}
          fullWidth={fullWidth}
          inputContainerClassName={styles.input}
        >
          <button
            type="button"
            className={classNames(styles.clearButton, {
              [styles.hide!]: !hasFiles || disabled || readOnly,
            })}
            onClick={handleClearFiles}
            aria-label="Clear selected file"
            tabIndex={tabIndex}
          >
            <Icon size="300" name="x" />
          </button>
        </TextInput>

        {/* Hidden file input */}
        <input
          {...props}
          ref={wrappedRef}
          type="file"
          id={inputId}
          name={name}
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={handleFileInputChange}
          className={styles.hiddenInput}
          aria-describedby={description ? `${inputId}-description` : undefined}
          tabIndex={-1}
        />

        <IconButton
          type="button"
          kind="secondary"
          size="default"
          disabled={disabled || readOnly}
          onClick={handleBrowseClick}
          className={styles.browseButton}
          iconName="upload"
          tabIndex={tabIndex}
        >
          {buttonText}
        </IconButton>
      </LayoutStack>
    )
  }
)

FileUploadInput.displayName = 'FileUploadInput'

export default FileUploadInput
